import { playCue } from "../lib/sound";
import { useEffect, useRef, useState, type ReactNode } from "react";
import * as ml from "../api/ml";
import { saveBlob } from "../api/export";
import { ModelImportRefused } from "../api/errors";
import { ClassResolutionDialog } from "../dialogs/ClassResolutionDialog";
import { HailoCompileDialog } from "../dialogs/HailoCompileDialog";
import { OnnxExportDialog } from "../dialogs/OnnxExportDialog";
import { importSummary, modelFilename, onnxFilename } from "../lib/training";
import { useSession } from "../state/session";
import { useStatus } from "../state/status";
import { useDialogs } from "./dialogs";
import { creditsChanged } from "../lib/credits";

/**
 * The buttons both the Train and Auto-Annotate pages offer around a trained model: save best.pt, export ONNX (of the latest version, or the one picked from `versions`) and
 * (auto-annotate only) import weights. Files arrive as browser downloads instead of save-as dialogs.
 */
export function useModelActions(project: { id: number; name: string; deployTarget?: ml.OnnxOptions["target"] | null }, version: number | null, onChanged: () => void,
  /** A version to start on (Models ▸ Auto-annotate with this version). */
  initialPick: number | null = null) {
  const dlg = useDialogs();
  const { has } = useSession();
  const { say } = useStatus();
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [importing, setImporting] = useState<null | "checking">(null);
  const [panel, setPanel] = useState<ReactNode>(null);

  // Which version Save and Export act on. null = whatever is latest, which is what these buttons always did.
  const [versions, setVersions] = useState<ml.ModelVersionInfo[]>([]);
  const [picked, setPicked] = useState<number | null>(initialPick);
  useEffect(() => {
    let alive = true;
    ml.listModels(project.id).then((r) => alive && setVersions(r)).catch(() => alive && setVersions([]));
    return () => { alive = false; };
  }, [project.id, version]);                       // `version` moves when training finishes or a model is imported
  // A new latest version (training finished, a model imported) is what a person means next; an older pick would
  // otherwise go on quietly applying to auto-annotation.
  // Only a change of the latest counts: its first read (null to the current latest) is not one, or a starting pick would be lost.
  const seen = useRef(version);
  useEffect(() => {
    if (seen.current !== null && seen.current !== version) setPicked(null);
    seen.current = version;
  }, [version]);
  const chosen = picked !== null && versions.some((v) => v.version_number === picked) ? picked : null;
  const target = chosen ?? version;

  /** Download a version's weights: the one picked (or latest), or `v` (a row on the Models page). */
  const saveVersion = async (v: number | null) => {
    setSaving(true);
    try {
      const name = modelFilename(v);
      saveBlob(await ml.downloadModel(project.id, v), name);
      say(`Weights downloaded as ${name}`);
    } catch (e) { await dlg.alert({ title: "Could not download the weights", text: (e as Error).message }); }
    finally { setSaving(false); }
  };

  const save = () => saveVersion(target);

  const runOnnx = async (o: ml.OnnxOptions, v: number | null) => {
    setPanel(null);
    setConverting(true);
    try {
      const name = onnxFilename(project.name, v, o);
      saveBlob(await ml.exportOnnx(project.id, o, v), name);
      // What to do with the file is in Details; the toast says it is done.
      const next = o.target === "hailo"
        ? `1. Compile it to a .hef on a machine with the Hailo Dataflow Compiler (README.txt has the commands; the desktop app can do it under Tools).\n2. Upload the .hef to the ctrlX detector app.\n\nUntil it is compiled, uploading this file to the current app runs it on the CPU.`
        : o.target === "tensorrt"
          ? `1. Unzip it on the GPU machine.\n2. Run:\n    pip install onnxruntime-gpu opencv-python\n    python run_nvidia.py --image <a frame>\n   It checks the model really runs on the GPU.\n3. Optional: build a TensorRT engine with the command in README.txt.`
          : "Upload it to your ctrlX app. The class names and pre-processing settings are stored inside it.";
      say(`Exported ${name}`, undefined, { title: "What happens next", text: `Saved as ${name}.\n\n${next}` });
      playCue("ready");
    } catch (e) { await dlg.alert({ title: "Could not export the model", text: (e as Error).message }); }
    finally { setConverting(false); creditsChanged(); }
  };

  /** The Export for deployment dialog for the version picked (or latest), or for `v` (a row on the Models page). */
  const exportVersion = async (v: number | null, label = v !== null ? `model v${v}` : undefined) => {
    // Whether this server has a Hailo compiler (and what the project's last compile did) only matters for the Hailo target;
    // an old server, or one without it, answers nothing useful and the dialog just has no Compile button.
    const hailo = has("hailo") ? await ml.hailoCompileInfo(project.id).catch(() => null) : null;
    const compile = (o: ml.OnnxOptions) => setPanel(
      <HailoCompileDialog projectId={project.id} projectName={project.name} version={v} calibLimit={o.calib_limit} onClose={() => setPanel(null)} />);
    // The thresholds the version measured, for the dialog to show: the row asked for, or the latest when none was.
    const row = v !== null ? versions.find((x) => x.version_number === v) : versions.find((x) => x.is_latest);
    setPanel(<OnnxExportDialog modelLabel={label} has={has} defaultTarget={project.deployTarget} recommended={row?.deploy ?? null} onCancel={() => setPanel(null)} onExport={(o) => void runOnnx(o, v)}
      onCompile={hailo?.enabled ? compile : undefined} compileJob={hailo?.job} />);
  };
  const openOnnx = () => exportVersion(target, chosen !== null ? `model v${chosen}` : undefined);

  const runImport = async (file: File, create: boolean, mapping?: Record<string, string>) => {
    setImporting("checking");
    try {
      const r = await ml.importModel(project.id, file, create, mapping);
      setImporting(null);
      say(`Imported as model v${r.version_number}`, undefined, { title: "Model imported", text: importSummary(r) });
      onChanged();
    } catch (e) {
      setImporting(null);
      if (e instanceof ModelImportRefused) {
        // 409: usable weights, but the project lacks classes the model predicts under these names.
        if (e.status === 409 && e.missingClasses.length) {
          setPanel(<ClassResolutionDialog missing={e.missingClasses} projectClasses={e.projectClasses} suggestions={e.suggestions}
            onCancel={() => setPanel(null)} onImport={(m) => { setPanel(null); void runImport(file, true, m); }} />);
          return;
        }
        // 400: this build cannot load the file at all.
        const reasons = e.reasons.length ? e.reasons : [e.message];
        await dlg.alert({ title: "This file cannot be used", text: `This file was not imported.\n\n${reasons.map((r) => `• ${r}`).join("\n\n")}` });
        return;
      }
      await dlg.alert({ title: "Could not import the model", text: (e as Error).message });
    }
  };

  /** A row the server answered with (Keep), in place of the one listed. */
  const replaceVersion = (v: ml.ModelVersionInfo) => setVersions((vs) => vs.map((x) => (x.version_number === v.version_number ? v : x)));
  return { save, saveVersion, saving, openOnnx, exportVersion, converting, runImport, importing, panel, versions, picked: chosen, setPicked, replaceVersion };
}
